import { CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Offer } from '@ooh/contracts';
import { ImportStepper } from '@ooh/ui/components/import-stepper';
import { canDownloadOrderDocument } from '../lib/approval-actions';
import {
  AcceptedConfirmAction,
  ConfirmedRecordSaleAction,
  ConfirmedUnsecuredSection,
  OrderDocumentAction,
  ReservePreliminaryAction,
} from './finalization-panel-actions';

/**
 * Design answer to CU-86cbc4uyu: after client acceptance, the confirm-campaign /
 * record-sale / download-order actions previously lived only as header buttons that
 * appeared and disappeared with status, so the salesperson couldn't tell where they
 * were in the process or what came next. This panel makes the three steps explicit
 * and owns the one primary action for the current step.
 */

type FinalizationStatus = Extract<
  Offer['status'],
  'DRAFT' | 'SENT' | 'ACCEPTED' | 'CONFIRMED' | 'SOLD'
>;

function isFinalizationStatus(status: Offer['status']): status is FinalizationStatus {
  return (
    status === 'DRAFT' ||
    status === 'SENT' ||
    status === 'ACCEPTED' ||
    status === 'CONFIRMED' ||
    status === 'SOLD'
  );
}

// Trash and status are orthogonal (offer-approval / "Finalization path in
// the offer workspace"): only a trashed DRAFT has no path to show, since a
// draft in the trash has no path at all. A trashed SENT/ACCEPTED/CONFIRMED
// offer keeps showing its panel exactly as before this requirement — the
// path it is already on does not stop applying just because it was trashed.
function isRenderableFinalizationStatus(
  status: Offer['status'],
  isTrashed: boolean,
): status is FinalizationStatus {
  return isFinalizationStatus(status) && !(isTrashed && status === 'DRAFT');
}

// `null` for DRAFT: the path hasn't started, so every step reads as upcoming
// rather than pointing at a (wrong) real step index (ImportStepper's
// `currentStep: number | null`).
const CURRENT_STEP: Record<FinalizationStatus, number | null> = {
  DRAFT: null,
  SENT: 0,
  ACCEPTED: 1,
  CONFIRMED: 2,
  SOLD: 3,
};

export interface FinalizationPanelProps {
  /** Only `status`/`campaignNumber` are needed — kept narrow so mockups and
   * other partial-offer contexts don't have to satisfy the full `Offer` shape. */
  status: Offer['status'];
  campaignNumber: Offer['campaignNumber'];
  /** Whether the offer is in the trash. Only hides the panel when `status`
   * is `DRAFT` — a trashed offer already sent, accepted, or confirmed keeps
   * showing its panel, since trash and status are otherwise orthogonal. */
  isTrashed: boolean;
  /** Whether a `DRAFT` offer has at least one nośnik — decides which of the
   * two draft lines is shown. Ignored for every other status. */
  hasDraftUnits: boolean;
  hasUnsecuredUnits: boolean;
  canConfirmCampaign: boolean;
  canRecordSale: boolean;
  /** Offer edit access (view+edit), read independently of `canConfirmCampaign`
   * because the preliminary reservation action is hidden entirely without it —
   * unlike confirmation, it grants no `approve_any`-style bypass
   * (offer-accept-reservation / "The action does not render for a caller with
   * no edit access at all"). */
  canEditOffer: boolean;
  /** `reservation.manage` — the second half of the preliminary-reservation gate. */
  canManageReservation: boolean;
  /** Whether the active accepted version has anything to reserve or release —
   * disables the action with an explanation when both halves are empty. */
  hasReservePreliminaryWork: boolean;
  /**
   * Whether the caller's access to this offer has finished resolving. While
   * `false`, a gated step renders neither its action nor a "you may not
   * perform this" note, because the workspace treats unresolved access as
   * fail-closed ("no") and asserting a denial on that basis would tell a
   * caller who may in fact act that they may not (offer-approval /
   * "Approval Action UI in Offer Workspace"). Source as
   * `auth.permissions !== null` — the same fail-closed-until-resolved
   * signal `deriveWorkspacePermissionFlags` already reads in
   * `routes/offers/$offerId/index.tsx`.
   */
  isAccessResolved: boolean;
  isDownloadingOrderDocument: boolean;
  onConfirmCampaign: () => void;
  onSecureRemaining: () => void;
  onRecordSale: () => void;
  onDownloadOrderDocument: () => void;
  onReservePreliminary: () => void;
}

export function FinalizationPanel({
  status,
  campaignNumber,
  isTrashed,
  hasDraftUnits,
  hasUnsecuredUnits,
  canConfirmCampaign,
  canRecordSale,
  canEditOffer,
  canManageReservation,
  hasReservePreliminaryWork,
  isAccessResolved,
  isDownloadingOrderDocument,
  onConfirmCampaign,
  onSecureRemaining,
  onRecordSale,
  onDownloadOrderDocument,
  onReservePreliminary,
}: FinalizationPanelProps) {
  const { t } = useTranslation('offers');

  if (!isRenderableFinalizationStatus(status, isTrashed)) return null;

  const currentStep = CURRENT_STEP[status];
  const stepLabels = [
    t('workspace.header.finalizationPanel.steps.clientAcceptance'),
    t('workspace.header.finalizationPanel.steps.campaignConfirmation'),
    t('workspace.header.finalizationPanel.steps.sale'),
  ];
  // Every offer is numbered at draft creation (offer-draft-numbering); a missing
  // number means a legacy offer, not an unconfirmed one (offer-order-document).
  const isDraft = campaignNumber == null;

  return (
    <section
      className="border-b border-border bg-muted/20 px-6 py-3"
      data-testid="finalization-panel"
      data-current-step={currentStep}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('workspace.header.finalizationPanel.title')}
        </span>
        <ImportStepper
          steps={stepLabels}
          currentStep={currentStep}
          ariaLabel={t('workspace.header.finalizationPanel.ariaLabel')}
          stepStatusLabels={{
            done: t('workspace.header.finalizationPanel.stepStatus.done'),
            current: t('workspace.header.finalizationPanel.stepStatus.current'),
            upcoming: t('workspace.header.finalizationPanel.stepStatus.upcoming'),
          }}
        />
      </div>

      <div className="mt-2.5 space-y-2 rounded-lg border border-border bg-background px-4 py-3">
        {status === 'DRAFT' ? (
          <p className="text-xs text-muted-foreground">
            {hasDraftUnits
              ? t('workspace.header.finalizationPanel.draft.readyToShare')
              : t('workspace.header.finalizationPanel.draft.needsUnits')}
          </p>
        ) : null}

        {status === 'SENT' ? (
          <>
            <div className="text-sm font-semibold text-foreground">
              {t('workspace.header.finalizationPanel.sent.title')}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('workspace.header.finalizationPanel.sent.description')}
            </p>
          </>
        ) : null}

        {status === 'ACCEPTED' ? (
          <>
            <div className="text-sm font-semibold text-foreground">
              {t('workspace.header.finalizationPanel.accepted.title')}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('workspace.header.finalizationPanel.accepted.description')}
            </p>
            <div className="flex flex-wrap items-center gap-2 pt-0.5">
              <ReservePreliminaryAction
                canEditOffer={canEditOffer}
                canManageReservation={canManageReservation}
                isAccessResolved={isAccessResolved}
                hasWork={hasReservePreliminaryWork}
                onReserve={onReservePreliminary}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-0.5">
              <AcceptedConfirmAction
                canConfirmCampaign={canConfirmCampaign}
                isAccessResolved={isAccessResolved}
                onConfirmCampaign={onConfirmCampaign}
              />
            </div>
          </>
        ) : null}

        {status === 'CONFIRMED' ? (
          <>
            <div className="text-sm font-semibold text-foreground">
              {t('workspace.header.finalizationPanel.confirmed.title')}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('workspace.header.finalizationPanel.confirmed.description', {
                campaignNumber: campaignNumber ?? '—',
              })}
            </p>
            <ConfirmedUnsecuredSection
              hasUnsecuredUnits={hasUnsecuredUnits}
              canConfirmCampaign={canConfirmCampaign}
              isAccessResolved={isAccessResolved}
              onSecureRemaining={onSecureRemaining}
            />
            <div className="flex flex-wrap items-center gap-2 pt-0.5">
              <ConfirmedRecordSaleAction
                canRecordSale={canRecordSale}
                isAccessResolved={isAccessResolved}
                onRecordSale={onRecordSale}
              />
            </div>
          </>
        ) : null}

        {status === 'SOLD' ? (
          <>
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
              {t('workspace.header.finalizationPanel.sold.title')}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('workspace.header.finalizationPanel.sold.description')}
            </p>
          </>
        ) : null}

        {canDownloadOrderDocument(status) ? (
          <OrderDocumentAction
            isDraft={isDraft}
            isDownloading={isDownloadingOrderDocument}
            onDownloadOrderDocument={onDownloadOrderDocument}
          />
        ) : null}
      </div>
    </section>
  );
}
